import { SourceLinks } from '@/components/ui/source-links';
import { Badge } from '@/components/ui/badge';
import { GraduationCap, BookOpen } from 'lucide-react';
import { DiscoveryCard } from './discovery-card';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { cn } from '@/lib/utils';

interface SourceTheoryCardProps {
    theory: any; // Using any for brevity
    isExpanded: boolean;
    onToggleExpansion: () => void;
}

export function SourceTheoryCard({
    theory,
    isExpanded,
    onToggleExpansion
}: SourceTheoryCardProps) {
    const isDarkMode = useDarkMode();

    return (
        <DiscoveryCard
            isExpanded={isExpanded}
            onToggle={onToggleExpansion}
            header={
                <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                        <GraduationCap className="h-5 w-5 text-primary/70" />
                        <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-serif font-medium text-lg text-foreground/90">{theory.theory}</h4>
                            <Badge
                                variant="outline"
                                className={`text-[9px] uppercase tracking-tighter ${theory.confidence === 'consensus' ? 'border-green-500/50 text-green-600' :
                                    theory.confidence === 'majority' ? 'border-blue-500/50 text-blue-600' :
                                        'border-yellow-500/50 text-yellow-600'
                                    }`}
                            >
                                {theory.confidence}
                            </Badge>
                        </div>
                    </div>
                    <p className="text-sm text-foreground/70 line-clamp-2 italic font-serif">
                        {theory.description?.split('\n\n')[0]}
                    </p>
                    <div className={cn(
                        "flex items-start gap-4 p-5 rounded-2xl mb-8 mt-6 -mb-4 border shadow-sm",
                        isDarkMode
                            ? "bg-amber-950/20 border-amber-500/30"
                            : "bg-amber-50/80 border-amber-200"
                    )}>
                        <div className={cn(
                            "p-2 rounded-xl shrink-0",
                            isDarkMode ? "bg-amber-500/20" : "bg-amber-500/10"
                        )}>
                            <BookOpen className={cn(
                                "w-4 h-4",
                                isDarkMode ? "text-amber-400" : "text-amber-600"
                            )} />
                        </div>
                        <div className="space-y-1.5 pt-0.5">
                            <h5 className={cn(
                                "text-[11px] font-bold uppercase tracking-[0.2em]",
                                isDarkMode ? "text-amber-300" : "text-amber-800"
                            )}>Source Theories</h5>
                            <p className={cn(
                                "text-xs leading-relaxed italic font-serif",
                                isDarkMode ? "text-amber-50/90" : "text-amber-950/80"
                            )}>
                                Source theories are often controversial. While they at times represent significant historical models in biblical criticism, many are no longer considered a scholarly consensus and remain highly debated.
                            </p>
                        </div>
                    </div>

                </div>
            }
        >
            <div className="space-y-4 mt-6">
                <p className="text-sm text-foreground/70 leading-relaxed font-light italic border-l-2 border-primary/20 pl-6 py-1">
                    {theory.description}
                </p>

                {theory.proposedSources?.length > 0 && (
                    <div className="space-y-4 pt-4 border-t border-border/10">
                        <div className="text-[10px] font-bold text-foreground/50 uppercase tracking-[0.2em]">Proposed Theoretical Sources</div>
                        <div className="space-y-4">
                            {theory.proposedSources.map((proposedSource: any, idx: number) => (
                                <div key={idx} className="pl-4 border-l-2 border-primary/10 space-y-2">
                                    <h6 className="font-serif font-medium text-base text-foreground/90">{proposedSource.sourceName}</h6>
                                    <p className="text-sm text-foreground/70 italic leading-relaxed">{proposedSource.description}</p>

                                    {proposedSource.informedSections?.length > 0 && (
                                        <div className="pt-2">
                                            <div className="flex gap-2 flex-wrap mb-2">
                                                {proposedSource.informedSections.map((section: any, sIdx: number) => (
                                                    <div key={sIdx} className="space-y-1">
                                                        <div className="flex gap-1 flex-wrap">
                                                            {section.passages.map((p: string, pIdx: number) => (
                                                                <Badge key={pIdx} variant="outline" className="text-[9px] uppercase tracking-tighter">
                                                                    {p}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                        <p className="text-[10px] text-muted-foreground italic">{section.description}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {theory.sources?.length > 0 && (
                    <div className="pt-4 border-t border-border/10">
                        <SourceLinks sources={theory.sources} />
                    </div>
                )}
            </div>
        </DiscoveryCard>
    );
}
